<script setup lang="ts">
import { computed, defineEmits, defineProps, RenderFunction, watch } from 'vue'
import BaseInputWrapper from './BaseInputWrapper.vue'
import { twMerge } from 'tailwind-merge'
import { useFieldHelper } from './useBaseInput'
import { InputSize } from '../../typings'

interface Props {
    modelValue: string | null
    label: string
    name: string
    icon?: RenderFunction
    rules?: any
    class?: string
    size?: InputSize
    standalone?: boolean
}
const props = withDefaults(defineProps<Props>(), { size: 'md' })
const { errorMessage, handleChange } = useFieldHelper(props.name, props.rules, {
    standalone: props.standalone ? props.standalone : false,
    initialValue: props.modelValue,
})

const emit = defineEmits(['update:modelValue'])

watch(
    () => props.modelValue,
    (value) => {
        handleChange(value)
    }
)

function updateValue(value) {
    handleChange(value)
    emit('update:modelValue', value)
}

const value = computed({
    get: () => props.modelValue,
    set: (value) => {
        updateValue(value)
    },
})

const iconClasses = computed(() => {
    return props.icon ? 'pl-10' : ''
})

const inputSizeClasses = computed(() => {
    const config: { [key: InputSize]: string } = {
        xs: 'text-xs py-1',
        sm: 'text-sm py-1',
        md: 'text-md py-2',
        lg: 'text-lg py-2',
        xl: 'text-xl sm:text-3xl py-4',
    }
    return config[props.size]
})

const errorMessageClasses = computed(() => {
    return errorMessage.value
        ? 'border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
        : ''
})
</script>

<template>
    <BaseInputWrapper :name="props.name" :label="props.label" :icon="icon">
        <input
            :id="props.name"
            v-bind="$attrs"
            v-model="value"
            type="number"
            :name="props.name"
            :class="
                twMerge(
                    'block w-full rounded-md border-gray-300 py-3 shadow-sm focus:border focus:border-primary focus:ring-primary disabled:bg-gray-100',
                    iconClasses,
                    errorMessageClasses,
                    inputSizeClasses,
                    props.class
                )
            "
            :aria-invalid="!!errorMessage" />
        <template #add-on>
            <slot name="add-on" />
        </template>
    </BaseInputWrapper>
</template>
